import React, { useMemo } from 'react';
import { GridColDef } from '@mui/x-data-grid';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';
import { RequestQuote } from "@mui/icons-material";
import Typography from '@mui/material/Typography';
import CustomDataGrid from '../../../../components/ui/customDataGrid/customDataGrid.component';
import { SETTLEMENTS_ENDPOINT, SETTLEMENT_STATUS_TO_COLOR } from '../../../../consts';
import { useHasPermission } from '../../../../contexts/auth';
import { formatCounterValue, formatNumber, getMonthNames } from '../../../../utils';
import { getSettlementEquipmentLabel, getSettlementEquipments } from '../../../../utils/settlementContractTerms';
import EquipmentReferenceList from '../../../../components/ui/entityReference/equipmentReferenceList.component';
import {
  equalsNumberFilterOperators,
  equalsStringFilterOperators,
} from '../../../../components/ui/customDataGrid/filterOperators';

export const ContractSettlements = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const canViewSettlements = useHasPermission("settlement.view_settlement");
  const canViewEquipment = useHasPermission("settlement.view_equipment");
  const monthNames = useMemo(() => getMonthNames(t), [t]);

  const columns: GridColDef[] = useMemo(() => [
    {
      field: "id",
      headerName: "ID",
      filterOperators: equalsNumberFilterOperators(),
    },
    {
      field: "equipment",
      headerName: t("Equipment"),
      sortable: false,
      filterable: false,
      valueGetter: (_value, row) => getSettlementEquipmentLabel(row),
      renderCell: (params) => (
        <EquipmentReferenceList
          equipments={getSettlementEquipments(params.row)}
          canPreview={canViewEquipment}
        />
      ),
    },
    {
      field: "status",
      headerName: t("Status"),
      filterOperators: equalsStringFilterOperators(),
      renderCell: (params) => {
        const color = SETTLEMENT_STATUS_TO_COLOR[params.row.status] || "default";
        return (
          <Chip
            label={params.row.status_display}
            color={color}
            size="small"
          />
        );
      },
    },
    {
      field: "month",
      headerName: t("Month"),
      filterable: false,
      valueFormatter: (value) => {
        if (!value) return "-";

        const [year, month] = String(value).split("-");
        return monthNames[Number(month)] ? `${monthNames[Number(month)]} ${year}` : value;
      },
    },
    {
      field: "black_counter_increase",
      headerName: t("Black counter increase"),
      type: "number",
      filterable: false,
      valueFormatter: (value) => formatCounterValue(value),
    },
    {
      field: "color_counter_increase",
      headerName: t("Color counter increase"),
      type: "number",
      filterable: false,
      valueFormatter: (value) => formatCounterValue(value),
    },
    {
      field: "usage_total",
      headerName: t("Total for Usage"),
      type: "number",
      filterable: false,
      valueFormatter: (value) => formatNumber(value),
    },
    {
      field: "total",
      headerName: t("Total"),
      type: "number",
      filterable: false,
      valueGetter: (_value, row) => row.total_overridden ?? row.total_calculated,
      renderCell: (params) => {
        const { total_calculated: calculated, total_overridden: overridden } = params.row;
        const hasOverride = overridden != null && overridden !== calculated;

        return hasOverride ? (
          <Box sx={{ display: "flex", flexDirection: "column", lineHeight: 1.2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ textDecoration: "line-through" }}>
              {formatNumber(calculated)}
            </Typography>
            <Typography variant="body2">{formatNumber(overridden)}</Typography>
          </Box>
        ) : formatNumber(params.value);
      },
    },
  ], [t, canViewEquipment, monthNames]);

  const handleRowClick = (settlementId: string) => {
    navigate(`/settlements/${settlementId}`);
  };

  if (!canViewSettlements) {
    return null;
  }

  const settlementsUrl = `${SETTLEMENTS_ENDPOINT}?contract_id=${id}`;
  return (
    <Box sx={{ height: '100%', width: '100%' }}>
      <CustomDataGrid
        icon={<RequestQuote />}
        title={t('Settlements')}
        url={settlementsUrl}
        gridSettingsKey="CONTRACT_SETTLEMENTS"
        columns={columns}
        onRowClick={handleRowClick}
        refreshEventName="settlementAdded"
      />
    </Box>
  );
};
